import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './AssignServiceProviderModal.css';
import { getViewerTimezone, instantToWallClockInZone } from '../../utils/dateTime';

// Bottom-sheet modal for assigning (or reassigning) a service provider to an
// existing service-typed calendar row on the transaction timeline — mirrors
// mobile's AssignSpModal.tsx (pick provider, date/time, Send Request).
//
// Pure presentation + local UX state (picker open/closed, selection,
// date/time inputs). All network calls are owned by the parent page and
// passed in as onSearch / onAssign props. onSearch is invoked with an empty
// term on picker-open to fetch every provider of the entry's service type.

// Company-vendor SPs display businessName; individuals fall through to
// fullName / firstName+lastName / email. Shared by the picker row and the
// selected-provider chip so they always agree.
const displayNameForProvider = (p) => {
  const isCompany = (p?.vendorType || '').toUpperCase() === 'COMPANY';
  const personalName = [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim();
  if (isCompany) {
    return p?.businessName || personalName || p?.fullName || p?.name || p?.email || 'Unnamed provider';
  }
  return p?.fullName || p?.name || personalName || p?.businessName || p?.email || 'Unnamed provider';
};

const AssignServiceProviderModal = ({ visible, entry, onClose, onSearch, onAssign }) => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  // Picker modal (nested) — replaces the pre-2026-08-31 inline expansion
  // panel. Opens on "Select Provider" click; closes on pick or backdrop.
  const [showProviderPickerModal, setShowProviderPickerModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // "Look outside the 25-mi radius" toggle. Starts false so the picker
  // shows nearby providers first; the requester flips it on to widen the
  // search. Passed to onSearch as a 3rd options arg so the parent's fetch
  // skips referenceLat/Lng and falls through to the state predicate.
  const [expandedSearch, setExpandedSearch] = useState(false);
  // Search input state — typing 3+ chars flips fetch to state-wide
  // name-lookup (drops radius filter, sends `name=<query>` to onSearch).
  // Mirrors CreateServiceRequestPage's provSearch pattern so requesters can
  // find an SP by name when the radius has none.
  const [providerSearchQuery, setProviderSearchQuery] = useState('');
  const [debouncedProviderSearch, setDebouncedProviderSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedProviderSearch(providerSearchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [providerSearchQuery]);
  const providerNameQuery = debouncedProviderSearch.length >= 3 ? debouncedProviderSearch : '';
  const seqRef = useRef(0);
  // "Already loaded for this entry" flag as a ref (NOT state) — mixing it
  // into the effect deps was causing an infinite cancel loop: setLoading(true)
  // → re-render → deps changed → effect re-runs → guard bails → prior run's
  // cleanup fires → cancelled=true → nothing ever completes. Refs don't
  // trigger re-renders, so the effect fires exactly once per open.
  const providersLoadedRef = useRef(false);

  // Reset/pre-fill whenever a new row is opened.
  //
  // Timezone handling: calendar events created post-2026-08-26 carry
  // `timeZone: 'UTC'` and store `scheduledDate` / `scheduledTime` as UTC
  // wall-clock digits (see TransactionDetailPage.handleAssignServiceProvider's
  // toUtcWallClock encode). Slicing those raw into the <input type=date> /
  // <input type=time> pickers would show the requester UTC digits (e.g.
  // "06:53 AM") instead of their profile-zone wall-clock (e.g. "11:53 PM
  // PDT"), which is exactly what the read-side already renders via
  // formatEventForViewer. Convert the stored UTC wall-clock back through an
  // instant into the viewer's profile zone so the pickers open on the same
  // wall-clock the timeline row above them displays. handleAssignServiceProvider
  // re-encodes the picker output via toUtcWallClock(viewerZone) on submit,
  // so the round-trip preserves the underlying instant.
  //
  // Legacy events without a timeZone marker fall through to the raw slice —
  // that path never encoded viewer-zone in the first place and shows the
  // same digits the timeline shows for that entry today.
  useEffect(() => {
    if (!visible || !entry) return;
    setProviders([]);
    setProvidersLoading(false);
    providersLoadedRef.current = false;
    setShowProviderPickerModal(false);
    setSelectedProvider(null);

    let prefillDate = '';
    let prefillTime = '';
    if (entry.scheduledDate) {
      if (entry.timeZone === 'UTC' && !entry.scheduledTime) {
        // Date-only event — no time of day to convert. Treating it as
        // midnight UTC would open the picker on the previous day for any US
        // viewer (and a submit would then move the SR's due date back a
        // day). Same literal-date rule as eventLocalDateKey /
        // formatEventForViewer.
        prefillDate = String(entry.scheduledDate).slice(0, 10);
        prefillTime = '';
      } else if (entry.timeZone === 'UTC') {
        const rawDate = String(entry.scheduledDate).slice(0, 10);
        const rawTime = entry.scheduledTime || '00:00';
        const [y, m, d] = rawDate.split('-').map(Number);
        const [hh, mm] = rawTime.split(':').map(Number);
        const utcInstant = new Date(Date.UTC(
          y, (m || 1) - 1, d || 1, hh || 0, mm || 0,
        )).toISOString();
        const { localDate, localTime } = instantToWallClockInZone(
          utcInstant, getViewerTimezone(),
        );
        prefillDate = localDate;
        prefillTime = entry.scheduledTime ? localTime : '';
      } else {
        // scheduledDate might be a UTC ISO like "2026-09-19T18:30:00Z"
        // (Sep-20 IST midnight); slicing before 'T' would return the UTC
        // portion "2026-09-19" and drift off-by-one against the timeline
        // row's local-timezone display. Use local Y-M-D for ISO strings so
        // the prefilled date matches the row the user just clicked.
        const raw = String(entry.scheduledDate);
        if (raw.includes('T')) {
          const parsed = new Date(raw);
          if (!isNaN(parsed.getTime())) {
            const yyyy = parsed.getFullYear();
            const mmm = String(parsed.getMonth() + 1).padStart(2, '0');
            const ddd = String(parsed.getDate()).padStart(2, '0');
            prefillDate = `${yyyy}-${mmm}-${ddd}`;
          } else {
            prefillDate = raw.slice(0, 10);
          }
        } else {
          prefillDate = raw.slice(0, 10);
        }
        prefillTime = entry.scheduledTime || '';
      }
    }
    setScheduledDate(prefillDate);
    setScheduledTime(prefillTime);
    setSubmitting(false);
    setError('');
    setExpandedSearch(false);
    setProviderSearchQuery('');
    setDebouncedProviderSearch('');
    seqRef.current++;
  }, [visible, entry]);

  // Clear the "loaded once" guard whenever the requester flips the
  // expanded-search toggle OR the debounced search query changes so the
  // fetch effect below re-runs with the new radius/name intent.
  useEffect(() => {
    providersLoadedRef.current = false;
  }, [expandedSearch, providerNameQuery]);

  // Lazy-load the provider list the first time the picker opens. Cached in
  // `providers` for the lifetime of this modal invocation so a close/reopen
  // is instant.
  useEffect(() => {
    if (!showProviderPickerModal || !entry) return undefined;
    if (providersLoadedRef.current) return undefined;
    const seq = ++seqRef.current;
    let cancelled = false;
    setProvidersLoading(true);
    (async () => {
      try {
        // Non-empty name auto-forces state-wide search (see
        // handleSearchServiceProviders in TransactionDetailPage — a name
        // filter only makes sense state-wide, otherwise a match > 25 mi
        // away is invisible to the requester who just typed it).
        const results = await onSearch(entry.entryType, providerNameQuery, {
          expandedSearch: expandedSearch || !!providerNameQuery,
          name: providerNameQuery || undefined,
        });
        if (cancelled || seq !== seqRef.current) return;
        setProviders(results || []);
        providersLoadedRef.current = true;
      } catch {
        if (!cancelled && seq === seqRef.current) setProviders([]);
      } finally {
        if (!cancelled && seq === seqRef.current) setProvidersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showProviderPickerModal, entry, onSearch, expandedSearch, providerNameQuery]);

  if (!visible || !entry) return null;

  const handleSelect = (provider) => {
    setSelectedProvider(provider);
    setShowProviderPickerModal(false);
  };

  const clearSelection = () => {
    setSelectedProvider(null);
  };

  const canSubmit = !!selectedProvider && !!scheduledDate && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await onAssign(selectedProvider, scheduledDate, scheduledTime);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedName = selectedProvider ? displayNameForProvider(selectedProvider) : '';

  // Portal into document.body so this overlay escapes .main-content's
  // z-index:1 stacking context — same rationale as ServiceRequestDetailsModal.
  // Without this, lp-overlay's z-index:1500 stays trapped inside the parent
  // stacking context and can't rise above the SR-details overlay when Assign
  // is opened from View Details. Symptom: Assign renders BEHIND SR details
  // and the Back button appears invisible (it's underneath the SR modal).
  return createPortal(
    <div className="lp-overlay" onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}>
      <div className="lp-container asp-container">
        <div className="asp-drag-handle" />
        <div className="lp-header">
          <span className="lp-title">Assign Service Provider</span>
          <button className="lp-close-btn" onClick={onClose} disabled={submitting} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <p className="lp-subtitle">{entry.title || entry.entryType}</p>

        <div className="asp-body">
          <label className="asp-section-label">Select Provider</label>
          {/* Trigger: shows selected name once picked, else "Select
              Provider" placeholder. Click opens the nested picker modal
              (Companies + Individuals rows with COMPANY badge + City,STATE).
              Matches AddTimelineEntry's provider flow. */}
          {selectedProvider ? (
            <div className="txn-provider-picked-row">
              <div className="txn-provider-picked-name">{selectedName}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="txn-provider-picked-change"
                  onClick={() => setShowProviderPickerModal(true)}
                  disabled={submitting}
                >
                  Change
                </button>
                <button
                  type="button"
                  className="txn-provider-picked-change"
                  onClick={clearSelection}
                  disabled={submitting}
                  aria-label="Clear selection"
                >
                  ×
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="txn-provider-select-btn"
              onClick={() => setShowProviderPickerModal(true)}
              disabled={submitting}
            >
              <span>Select Provider</span>
              <span className="txn-provider-select-chevron">▾</span>
            </button>
          )}

          {selectedProvider && (
            <div className="asp-selected-provider">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <span>{selectedName} will receive the request.</span>
            </div>
          )}

          {/* Time input was removed platform-wide (Due-By-Time input dropped;
               deadline is end-of-day only). Single "Due By" date field. */}
          <div className="txn-form-group">
            <label className="txn-form-label">Due By Date</label>
            <input
              type="date"
              className="txn-form-input"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <div className="txn-modal-error" style={{ color: '#dc3545', background: '#fdf0f0', padding: '10px 16px', borderRadius: '6px', fontSize: '14px' }}>
              {error}
            </div>
          )}
        </div>

        <div className="asp-footer">
          <button className="asp-cancel-btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="asp-submit-btn" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Sending…' : 'Send Request'}
          </button>
        </div>
      </div>

      {/* Provider Picker Modal — same styling / row layout as
          AddTimelineEntry's picker (Companies + Individuals with COMPANY
          badge, serviceType, City,STATE). Rendered as a sibling overlay
          inside the outer lp-overlay so its backdrop stacks correctly. */}
      {showProviderPickerModal && (
        <div
          className="txn-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowProviderPickerModal(false); }}
          style={{ zIndex: 10 }}
        >
          <div className="txn-provider-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="txn-provider-picker-header">
              {(providerSearchQuery || expandedSearch) ? (
                <button
                  type="button"
                  className="asp-back-btn"
                  onClick={() => {
                    // Reset the picker's filter state to the initial nearby
                    // view — clears both the "Show all providers" expand
                    // toggle and the search input. The list refetches to
                    // the pre-search radius view (see the picker's fetch
                    // effect on [expandedSearch, providerNameQuery]).
                    setProviderSearchQuery('');
                    setDebouncedProviderSearch('');
                    setExpandedSearch(false);
                  }}
                  aria-label="Back to nearby providers"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  <span>Back</span>
                </button>
              ) : (
                <span />
              )}
              <h3 className="txn-provider-picker-title">
                Select {entry.entryType || 'Provider'}
              </h3>
              <button
                type="button"
                className="txn-provider-picker-close"
                onClick={() => setShowProviderPickerModal(false)}
                aria-label="Close"
              >×</button>
            </div>
            <div className="txn-provider-picker-body">
              {/* Search input — typing 3+ chars flips fetch to state-wide
                  name-lookup so requesters can find an SP by name even when
                  the nearby radius has none. Mirrors CreateServiceRequestPage. */}
              <div className="txn-provider-picker-search">
                <input
                  type="text"
                  className="txn-provider-picker-search-input"
                  placeholder="Search by name…"
                  value={providerSearchQuery}
                  onChange={(e) => setProviderSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              {providersLoading ? (
                <div className="txn-provider-picker-loading">Loading providers…</div>
              ) : providers.length === 0 ? (
                <div className="txn-provider-picker-empty">
                  <p className="txn-provider-picker-empty-text">
                    {providerNameQuery
                      ? `No providers found matching "${providerNameQuery}".`
                      : expandedSearch
                        ? 'No providers found — invite one manually.'
                        : 'No providers found near this property.'}
                  </p>
                  {!expandedSearch && !providerNameQuery && (
                    <button
                      type="button"
                      className="txn-provider-picker-expand-btn"
                      onClick={() => setExpandedSearch(true)}
                    >
                      Show all providers
                    </button>
                  )}
                  <button
                    type="button"
                    className="txn-provider-picker-invite-btn"
                    onClick={() => {
                      setShowProviderPickerModal(false);
                      onClose && onClose();
                      // /invite-someone is the generic invite page open to
                      // Agents/Brokers/TCs (see InviteSomeonePage's
                      // INVITE_PERMISSIONS — AGENT can invite SERVICE_PROVIDER).
                      // Pre-2026-09-29 this navigated to /invite-inspector,
                      // which is the Company-SP-only page to invite in-house
                      // inspectors — its role guard bounces Agents to /dashboard.
                      // prefillRole 'SERVICE_PROVIDER' pre-selects the role dropdown
                      // so the requester lands directly on the provider-invite form.
                      navigate('/invite-someone', { state: { prefillRole: 'SERVICE_PROVIDER' } });
                    }}
                  >
                    Invite a Provider
                  </button>
                </div>
              ) : (
                providers.map((p, i) => {
                  const isCompany = (p.vendorType || '').toUpperCase() === 'COMPANY';
                  // Locality fallback chain: city+state → state alone → licenseState.
                  // SPs registered pre-address-refactor often have blank city/state
                  // but licenseState set; without the fallback the row would show
                  // no location context at all.
                  const stateUpper = (p.state || p.licenseState || '').toUpperCase();
                  const locality = [p.city, stateUpper].filter(Boolean).join(', ');
                  // Phase-4 SP-radius — backend populates distanceMiles only in
                  // radius mode; nullable for the state-path fallback. Guard
                  // against Number(null)===0 rendering "0.0 mi away" on every row.
                  const distanceLabel = (p.distanceMiles != null && Number.isFinite(Number(p.distanceMiles)))
                    ? `${Number(p.distanceMiles).toFixed(1)} mi away`
                    : null;
                  return (
                    <div
                      key={p.id || p.userId || i}
                      className="txn-provider-picker-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelect(p)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSelect(p)}
                    >
                      <div className="txn-provider-picker-name-row">
                        <span className="txn-provider-picker-name">{displayNameForProvider(p)}</span>
                        {isCompany && (
                          <span className="txn-provider-picker-badge">COMPANY</span>
                        )}
                        {distanceLabel && (
                          <span className="txn-provider-picker-badge txn-provider-picker-distance">
                            {distanceLabel}
                          </span>
                        )}
                      </div>
                      {p.serviceType && (
                        <div className="txn-provider-picker-meta">{p.serviceType}</div>
                      )}
                      {locality && (
                        <div className="txn-provider-picker-meta txn-provider-picker-locality">
                          {locality}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              {!expandedSearch && !providerNameQuery && providers.length > 0 && (
                <button
                  type="button"
                  className="txn-provider-picker-expand-btn"
                  onClick={() => setExpandedSearch(true)}
                >
                  Not finding what you need? Show all providers
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
};

export default AssignServiceProviderModal;
